/**
 * Create Study Plan Wizard Component
 * Multi-step wizard for generating a personalized study plan
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useLearningProfileContext } from '@/contexts/LearningProfileContext';
import { useGenerateStudyPlan } from '@/hooks/use-study-plan';
import { useToast } from '@/hooks/use-toast';
import {
  PLAN_TYPE_LABELS,
  DEFAULT_TIME_AVAILABILITY,
  type StudyPlanType,
  type TimeAvailability,
} from '@/types/study-plan';
import {
  CalendarDays,
  Clock,
  GraduationCap,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Sparkles,
  CheckCircle2,
  BookOpen,
  FileUp,
  CheckSquare,
} from 'lucide-react';

interface CreateStudyPlanWizardProps {
  onComplete?: (planId: string) => void;
  onCancel?: () => void;
  onUploadSyllabi?: () => void;
  syllabusId?: string;
}

type WizardStep = 'syllabi_check' | 'type' | 'subjects' | 'schedule' | 'generating' | 'complete';

// Available subjects for study
const AVAILABLE_SUBJECTS = [
  { id: 'contracts', label: 'Contracts' },
  { id: 'torts', label: 'Torts' },
  { id: 'civ_pro', label: 'Civil Procedure' },
  { id: 'property', label: 'Property' },
  { id: 'crim_law', label: 'Criminal Law' },
  { id: 'crim_pro', label: 'Criminal Procedure' },
  { id: 'con_law', label: 'Constitutional Law' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'wills_trusts', label: 'Wills & Trusts' },
  { id: 'family_law', label: 'Family Law' },
  { id: 'corporations', label: 'Corporations' },
  { id: 'secured_trans', label: 'Secured Transactions' },
] as const;

/**
 * Calculate and format weekly study time based on availability
 */
function formatWeeklyTime(ta: TimeAvailability): string {
  const weekdayCount = ta.preferred_days.filter((d) => d >= 1 && d <= 5).length;
  const weekendDays = ta.preferred_days.filter((d) => d === 0 || d === 6).length;

  let weekendMultiplier = 1;
  if (ta.weekend_pattern === 'every_other') weekendMultiplier = 0.5;
  if (ta.weekend_pattern === 'none') weekendMultiplier = 0;

  const weekdayTotal = weekdayCount * ta.weekday_minutes;
  const weekendTotal = weekendDays * ta.weekend_minutes * weekendMultiplier;
  const totalMinutes = weekdayTotal + weekendTotal;
  const totalHours = totalMinutes / 60;

  const weekdayHours = Math.round(weekdayTotal / 60);
  const weekendHours = Math.round(weekendTotal / 60);

  let summary = `~${totalHours.toFixed(1)} hours/week`;
  if (weekdayCount > 0) {
    summary += ` (${weekdayHours}h weekdays`;
  }
  if (weekendDays > 0 && weekendMultiplier > 0) {
    summary += weekdayCount > 0 ? ` + ${weekendHours}h weekends)` : ` (${weekendHours}h weekends)`;
  } else if (weekdayCount > 0) {
    summary += ')';
  }

  return summary;
}

export function CreateStudyPlanWizard({
  onComplete,
  onCancel,
  onUploadSyllabi,
  syllabusId,
}: CreateStudyPlanWizardProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useLearningProfileContext();
  const generatePlan = useGenerateStudyPlan();

  // Wizard state
  const [step, setStep] = useState<WizardStep>('syllabi_check');
  const [planType, setPlanType] = useState<StudyPlanType>('general');
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [examDate, setExamDate] = useState<string>('');
  const [timeAvailability, setTimeAvailability] = useState<TimeAvailability>(
    profile?.time_availability ?? DEFAULT_TIME_AVAILABILITY
  );
  const [generatedPlanId, setGeneratedPlanId] = useState<string | null>(null);

  const handleSubjectToggle = (subjectId: string) => {
    setSelectedSubjects((prev) =>
      prev.includes(subjectId)
        ? prev.filter((s) => s !== subjectId)
        : [...prev, subjectId]
    );
  };

  const handleDayToggle = (day: number) => {
    setTimeAvailability((prev) => ({
      ...prev,
      preferred_days: prev.preferred_days.includes(day)
        ? prev.preferred_days.filter((d) => d !== day)
        : [...prev.preferred_days, day].sort(),
    }));
  };

  const handleGenerate = async () => {
    setStep('generating');

    try {
      const plan = await generatePlan.mutateAsync({
        subjects: selectedSubjects,
        exam_date: examDate || undefined,
        plan_type: planType,
        syllabus_ids: syllabusId ? [syllabusId] : undefined,
        time_availability: timeAvailability,
        generate_phased: planType === 'bar_prep' || planType === 'exam_prep',
      });

      setGeneratedPlanId(plan.id);
      setStep('complete');
    } catch (error) {
      toast({
        title: 'Failed to generate plan',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      });
      setStep('schedule'); // Go back to last step
    }
  };

  const handleComplete = () => {
    if (generatedPlanId) {
      if (onComplete) {
        onComplete(generatedPlanId);
      } else {
        navigate(`/study-plan/${generatedPlanId}`);
      }
    }
  };

  const handleBack = () => {
    if (step === 'type') setStep('syllabi_check');
    else if (step === 'subjects') setStep('type');
    else if (step === 'schedule') setStep('subjects');
  };

  const handleNext = () => {
    if (step === 'syllabi_check') setStep('type');
    else if (step === 'type') setStep('subjects');
    else if (step === 'subjects') setStep('schedule');
    else if (step === 'schedule') handleGenerate();
  };

  const handleUploadClick = () => {
    if (onUploadSyllabi) {
      onUploadSyllabi();
    } else {
      // Default: navigate to syllabi upload page
      navigate('/dashboard/classes/syllabi/list');
    }
  };

  const canProceed = () => {
    if (step === 'syllabi_check') return true;
    if (step === 'type') return true;
    if (step === 'subjects') return selectedSubjects.length > 0;
    if (step === 'schedule') {
      return (
        timeAvailability.preferred_days.length > 0 &&
        timeAvailability.weekday_minutes >= 15 &&
        timeAvailability.weekend_minutes >= 15
      );
    }
    return false;
  };

  const getStepProgress = () => {
    const steps: WizardStep[] = ['syllabi_check', 'type', 'subjects', 'schedule'];
    const currentIndex = steps.indexOf(step);
    return ((currentIndex + 1) / steps.length) * 100;
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-primary" />
          Create Study Plan
        </CardTitle>
        {step !== 'generating' && step !== 'complete' && (
          <Progress value={getStepProgress()} className="h-1.5 mt-3" />
        )}
      </CardHeader>

      <CardContent>
        {/* Step 0: Syllabi Check */}
        {step === 'syllabi_check' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Have you uploaded all your course syllabi? This helps us create a more accurate study plan tailored to your actual coursework.
            </p>
            <div className="grid gap-3">
              <button
                type="button"
                onClick={handleNext}
                className="p-4 rounded-lg border-2 border-border hover:border-primary/50 text-left transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
                    <CheckSquare className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium">Yes, I'm ready to continue</p>
                    <p className="text-xs text-muted-foreground">
                      My syllabi are uploaded and I'm ready to create my plan
                    </p>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={handleUploadClick}
                className="p-4 rounded-lg border-2 border-border hover:border-primary/50 text-left transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
                    <FileUp className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">No, I need to upload syllabi first</p>
                    <p className="text-xs text-muted-foreground">
                      Take me to the syllabus upload area
                    </p>
                  </div>
                </div>
              </button>
            </div>
            <p className="text-xs text-muted-foreground italic">
              Tip: Uploading syllabi lets us extract your exam dates, topics, and assignments automatically.
            </p>
          </div>
        )}

        {/* Step 1: Plan Type */}
        {step === 'type' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              What kind of study plan do you need?
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {(Object.keys(PLAN_TYPE_LABELS) as StudyPlanType[]).map((type) => (
                <button
                  type="button"
                  key={type}
                  onClick={() => setPlanType(type)}
                  className={cn(
                    'p-4 rounded-lg border-2 text-left transition-all',
                    planType === type
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'p-2 rounded-lg',
                        planType === type ? 'bg-primary/10' : 'bg-muted'
                      )}
                    >
                      {type === 'exam_prep' || type === 'finals' ? (
                        <GraduationCap className="h-5 w-5 text-primary" />
                      ) : type === 'bar_prep' ? (
                        <BookOpen className="h-5 w-5 text-primary" />
                      ) : (
                        <CalendarDays className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{PLAN_TYPE_LABELS[type]}</p>
                      <p className="text-xs text-muted-foreground">
                        {type === 'general' && 'Ongoing semester study'}
                        {type === 'exam_prep' && 'Focused exam preparation'}
                        {type === 'bar_prep' && 'Bar exam intensive'}
                        {type === 'finals' && 'Finals week crunch'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Subjects */}
        {step === 'subjects' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Which subjects do you want to include?
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {AVAILABLE_SUBJECTS.map((subject) => (
                <label
                  key={subject.id}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                    selectedSubjects.includes(subject.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  <Checkbox
                    checked={selectedSubjects.includes(subject.id)}
                    onCheckedChange={() => handleSubjectToggle(subject.id)}
                  />
                  <span className="text-sm">{subject.label}</span>
                </label>
              ))}
            </div>
            {selectedSubjects.length === 0 && (
              <p className="text-xs text-amber-600">Please select at least one subject</p>
            )}
          </div>
        )}

        {/* Step 3: Schedule */}
        {step === 'schedule' && (
          <div className="space-y-6">
            {/* Exam date (optional) */}
            {(planType === 'exam_prep' || planType === 'finals' || planType === 'bar_prep') && (
              <div className="space-y-2">
                <Label htmlFor="exam-date">Exam Date</Label>
                <Input
                  id="exam-date"
                  type="date"
                  value={examDate}
                  onChange={(e) => setExamDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-muted-foreground">
                  Your plan will be optimized for this date
                </p>
              </div>
            )}

            {/* Weekday study time */}
            <div className="space-y-2">
              <Label>Weekday Study Time</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  value={timeAvailability.weekday_minutes}
                  onChange={(e) =>
                    setTimeAvailability((prev) => ({
                      ...prev,
                      weekday_minutes: Math.max(15, Number(e.target.value)),
                    }))
                  }
                  min={15}
                  max={480}
                  step={15}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">minutes per day (Mon-Fri)</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Recommended: 60-120 minutes for effective weekday learning
              </p>
            </div>

            {/* Weekend study time */}
            <div className="space-y-2">
              <Label>Weekend Study Time</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  value={timeAvailability.weekend_minutes}
                  onChange={(e) =>
                    setTimeAvailability((prev) => ({
                      ...prev,
                      weekend_minutes: Math.max(15, Number(e.target.value)),
                    }))
                  }
                  min={15}
                  max={720}
                  step={30}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">minutes per day (Sat/Sun)</span>
              </div>
            </div>

            {/* Weekend pattern */}
            <div className="space-y-2">
              <Label id="weekend-pattern-label">Weekend Availability</Label>
              <div
                className="grid gap-2 sm:grid-cols-3"
                role="radiogroup"
                aria-labelledby="weekend-pattern-label"
              >
                {[
                  { value: 'every', label: 'Every Weekend', desc: 'Available Sat & Sun each week' },
                  { value: 'every_other', label: 'Every Other', desc: 'Alternate weekends only' },
                  { value: 'none', label: 'Weekdays Only', desc: 'No weekend studying' },
                ].map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    onClick={() =>
                      setTimeAvailability((prev) => ({
                        ...prev,
                        weekend_pattern: option.value as TimeAvailability['weekend_pattern'],
                      }))
                    }
                    className={cn(
                      'p-3 rounded-lg border text-left transition-all',
                      timeAvailability.weekend_pattern === option.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    )}
                    role="radio"
                    aria-checked={timeAvailability.weekend_pattern === option.value}
                  >
                    <p className="text-sm font-medium">{option.label}</p>
                    <p className="text-xs text-muted-foreground">{option.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Available days */}
            <div className="space-y-2">
              <Label id="available-days-label">Available Days</Label>
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-labelledby="available-days-label"
                aria-describedby="available-days-hint"
              >
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                  <button
                    type="button"
                    key={day}
                    onClick={() => handleDayToggle(index)}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                      timeAvailability.preferred_days.includes(index)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    )}
                    aria-pressed={timeAvailability.preferred_days.includes(index)}
                    aria-label={`${day}${timeAvailability.preferred_days.includes(index) ? ' (selected)' : ''}`}
                  >
                    {day}
                  </button>
                ))}
              </div>
              <p id="available-days-hint" className="text-xs text-muted-foreground">
                Select the days you can commit to studying
              </p>
            </div>

            {/* Time summary */}
            <div className="p-4 rounded-lg bg-muted/50 border">
              <p className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Your Weekly Study Budget
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {formatWeeklyTime(timeAvailability)}
              </p>
            </div>
          </div>
        )}

        {/* Generating */}
        {step === 'generating' && (
          <div className="py-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <p className="mt-4 font-medium">Generating your personalized plan...</p>
            <p className="text-sm text-muted-foreground mt-1">
              Analyzing your profile, diagnostics, and learning goals
            </p>
          </div>
        )}

        {/* Complete */}
        {step === 'complete' && (
          <div className="py-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-medium">Your study plan is ready!</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-6">
              We've created a personalized schedule based on your goals.
            </p>
            <Button onClick={handleComplete}>
              <Sparkles className="h-4 w-4 mr-2" />
              View My Plan
            </Button>
          </div>
        )}

        {/* Navigation buttons */}
        {step !== 'generating' && step !== 'complete' && step !== 'syllabi_check' && (
          <div className="flex items-center justify-between mt-8 pt-4 border-t">
            <Button
              variant="ghost"
              onClick={step === 'type' ? onCancel : handleBack}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {step === 'type' ? 'Cancel' : 'Back'}
            </Button>
            <Button onClick={handleNext} disabled={!canProceed()}>
              {step === 'schedule' ? (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Plan
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Syllabi check has its own navigation via the buttons */}
        {step === 'syllabi_check' && (
          <div className="flex justify-start mt-8 pt-4 border-t">
            <Button variant="ghost" onClick={onCancel}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CreateStudyPlanWizard;
